import Bugsnag from '@bugsnag/js';
import { useCallback, useEffect, useRef } from 'react';
import {
  captureTypedPosthogEvent,
  type ExportEntryPoint,
  POSTHOG_EVENTS,
  type PosthogEventPropertyMap,
} from '~/common/posthogEvents';
import downloadStore from './store';

/**
 * Shared load telemetry for ExportHistory, the extract (FME) form, and the legacy download form
 * (which shares newDownloadStore with the FME form).
 */

export const SLOW_LOAD_THRESHOLD_MS = 60_000;

export type ExportFormDataResource =
  | 'projections'
  | 'templates'
  | 'project'
  | 'restrictions'
  | 'digitization_access'
  | 'layer_asset_presence';

/** Reports a failed supporting-data load on a download/export form, alongside the user-facing toast. */
export function reportExportFormDataLoadFailure(
  projectId: string,
  resource: ExportFormDataResource,
  error: unknown,
): void {
  captureTypedPosthogEvent(POSTHOG_EVENTS.exportFormDataLoadFailed, { project_id: projectId, resource });
  Bugsnag.notify(error instanceof Error ? error : new Error(`Export form ${resource} load failed: ${String(error)}`));
}

export type CustomDataLoadSurface = 'form' | 'history';

/** Reports a custom-data-list fetch failure. */
export function reportCustomDataLoadFailure(projectId: string, surface: CustomDataLoadSurface, error: unknown): void {
  captureTypedPosthogEvent(POSTHOG_EVENTS.exportCustomDataLoadFailed, { project_id: projectId, surface });
  Bugsnag.notify(
    error instanceof Error ? error : new Error(`Export custom-data load failed (${surface}): ${String(error)}`),
  );
}

/** Whether a custom-data failure should report now, and what key to remember for next time. */
export function evaluateCustomDataFailureReport(
  hasError: boolean,
  shouldReport: boolean,
  projectId: string,
  lastReportedKey: string | null,
): { shouldNotify: boolean; nextKey: string | null } {
  if (!hasError || !shouldReport) return { shouldNotify: false, nextKey: null };
  if (lastReportedKey === projectId) return { shouldNotify: false, nextKey: lastReportedKey };
  return { shouldNotify: true, nextKey: projectId };
}

/**
 * Reports a custom-data-list failure once per project, not on every poll tick — query error state
 * is shared across every observer on this key regardless of `enabled`, so this reacts to it
 * directly rather than depending on whichever observer's queryFn actually runs.
 */
export function useReportCustomDataLoadFailure(
  projectId: string,
  surface: CustomDataLoadSurface,
  error: unknown,
  shouldReport = true,
): void {
  const reportedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const { shouldNotify, nextKey } = evaluateCustomDataFailureReport(
      Boolean(error),
      shouldReport,
      projectId,
      reportedKeyRef.current,
    );
    reportedKeyRef.current = nextKey;
    if (shouldNotify) reportCustomDataLoadFailure(projectId, surface, error);
  }, [projectId, surface, error, shouldReport]);
}

export type ExportHistoryLoadPhase = 'initial' | 'poll';

export type ExportHistoryLoadOutcome = PosthogEventPropertyMap[typeof POSTHOG_EVENTS.exportHistoryLoaded]['outcome'];

/**
 * Fetches the history and reports how it went. Wraps `getProjectDownloads` (which itself has no
 * error handling) so a rejected fetch reports through PostHog/Bugsnag instead of becoming an
 * unhandled promise rejection.
 */
export async function loadExportHistory(
  projectId: string,
  phase: ExportHistoryLoadPhase,
): Promise<ExportHistoryLoadOutcome> {
  const startedAt = performance.now();
  // Fires while the fetch is still in flight; cleared below on any exit.
  const slowLoadTimer = setTimeout(() => {
    captureTypedPosthogEvent(POSTHOG_EVENTS.exportHistorySlowLoadDetected, {
      project_id: projectId,
      phase,
      elapsed_ms: SLOW_LOAD_THRESHOLD_MS,
    });
  }, SLOW_LOAD_THRESHOLD_MS);

  try {
    await downloadStore.getProjectDownloads(projectId);
    // Only the initial load's success is reported — polling repeats it indefinitely otherwise.
    if (phase === 'initial') {
      captureTypedPosthogEvent(POSTHOG_EVENTS.exportHistoryLoaded, {
        project_id: projectId,
        phase,
        outcome: 'success',
        duration_ms: Math.round(performance.now() - startedAt),
      });
    }
    return 'success';
  } catch (e) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.exportHistoryLoaded, {
      project_id: projectId,
      phase,
      outcome: 'failure',
      duration_ms: Math.round(performance.now() - startedAt),
    });
    Bugsnag.notify(e instanceof Error ? e : new Error(`Export history fetch failed: ${String(e)}`));
    return 'failure';
  } finally {
    clearTimeout(slowLoadTimer);
  }
}

/**
 * Reports that a just-submitted export's rows never landed within the wait window, then clears
 * the awaiting-submission state so the panel stops waiting — unlike its siblings above, this is
 * not pure telemetry.
 */
export function reportAwaitingSubmissionTimeout(projectId: string, submissionId: string): void {
  captureTypedPosthogEvent(POSTHOG_EVENTS.exportHistoryAwaitingSubmissionTimedOut, {
    project_id: projectId,
    submission_id: submissionId,
  });
  downloadStore.setAwaitingSubmission(null);
}

/**
 * Returns a stable reporter for the history's first fetch outcome that sends `export_tab_loaded`, timed from mount.
 *
 * It sends at most once and only after `formVisible` has been true, dropping earlier calls; switching projects before
 * it sends restarts the clock. `entryPoint` says how the user reached the tab.
 */
export function useExportTabLoadTelemetry(
  projectId: string,
  formVisible: boolean,
  entryPoint: ExportEntryPoint = 'other',
): (outcome: ExportHistoryLoadOutcome) => void {
  const startedAtRef = useRef(performance.now());
  const formReadyMsRef = useRef<number | null>(null);
  const sentRef = useRef(false);
  const projectIdRef = useRef(projectId);
  // Fixed for the visit: the caller reads it once on arrival.
  const entryPointRef = useRef(entryPoint);

  useEffect(() => {
    if (projectIdRef.current === projectId) return;
    projectIdRef.current = projectId;
    startedAtRef.current = performance.now();
    formReadyMsRef.current = null;
  }, [projectId]);

  useEffect(() => {
    if (formVisible && formReadyMsRef.current === null) {
      formReadyMsRef.current = Math.round(performance.now() - startedAtRef.current);
    }
  }, [formVisible]);

  return useCallback((outcome: ExportHistoryLoadOutcome) => {
    if (sentRef.current || formReadyMsRef.current === null) return;
    sentRef.current = true;
    captureTypedPosthogEvent(POSTHOG_EVENTS.exportTabLoaded, {
      project_id: projectIdRef.current,
      entry_point: entryPointRef.current,
      duration_ms: Math.round(performance.now() - startedAtRef.current),
      form_ready_ms: formReadyMsRef.current,
      history_outcome: outcome,
    });
  }, []);
}
